/**
 * Composite pre-lead redirect params.
 *
 * Some lead-buying partners (e.g. UMA) require several Dreambound IDs to be
 * bundled into a *single* query parameter using a labeled, delimited format
 * rather than each ID getting its own param. For example UMA wants:
 *
 *   ?vendorleadid=marketplace_event_id:abc123%7Ccustomer_id:def456%7Cef_transaction_id:xyz789
 *
 * where the pipe delimiter MUST be URL-encoded as %7C and the colon label
 * separators stay literal.
 *
 * This module is the single source of truth for the composite-param shape,
 * its validation, and its exact encoding. It is intentionally generic: any
 * program can declare one or more composite params via
 * `ProgramMetadata.preLeadRedirectCompositeParams`, and they coexist with the
 * simple one-ID-per-param `preLeadRedirectParams` mapping.
 *
 * Encoding contract (see assembleCompositeParam):
 *   - labels and values are individually `encodeURIComponent`-encoded so a
 *     value can never break the structure or inject extra params
 *   - the label separator is kept literal (default ":")
 *   - components are joined by the `encodeURIComponent`-encoded delimiter
 *     (default "|" -> "%7C")
 *
 * The assembled string is appended verbatim to the redirect query and then
 * base64url-wrapped by `getUrls().PreLeadRedirect` into the `/outbound?r=`
 * link, so what we assemble here is exactly what the partner receives.
 */

/**
 * The tracking IDs that may be referenced as a composite component `source`.
 * These are exactly the values `getUrls().PreLeadRedirect` already resolves.
 *
 * Note: `userZip` is only populated on the dream-search-click surface; on the
 * form-funnel clickout surface it is not available and a component referencing
 * it will render blank. UMA does not use `userZip`, so this asymmetry is
 * documented rather than addressed (the assembly guards by emitting a blank
 * value for any missing source).
 */
export const PRE_LEAD_REDIRECT_COMPOSITE_SOURCES = [
  "marketplaceEventId",
  "customerId",
  "everflowTransactionId",
  "programSlug",
  "userZip",
] as const;

export type PreLeadRedirectCompositeSource =
  (typeof PRE_LEAD_REDIRECT_COMPOSITE_SOURCES)[number];

export type PreLeadRedirectCompositeComponent = {
  /** Literal label shown before the value, e.g. "marketplace_event_id". */
  label: string;
  /** Which resolved tracking ID supplies the value. */
  source: PreLeadRedirectCompositeSource;
};

export type PreLeadRedirectCompositeParam = {
  /** The single query-param key the bundle is written to, e.g. "vendorleadid". */
  paramName: string;
  /** Separator between components. Default "|". Rendered percent-encoded. */
  delimiter?: string;
  /** Separator between a component's label and value. Default ":". Literal. */
  labelSeparator?: string;
  components: PreLeadRedirectCompositeComponent[];
};

export type PreLeadRedirectCompositeSourceValues = Partial<
  Record<PreLeadRedirectCompositeSource, string | undefined>
>;

const DEFAULT_DELIMITER = "|";
const DEFAULT_LABEL_SEPARATOR = ":";

/**
 * paramName must be a URL-safe query key. Label separators are kept literal so
 * they must not contain characters that would corrupt the query structure.
 */
const PARAM_NAME_PATTERN = /^[A-Za-z0-9_.-]+$/;
const LABEL_SEPARATOR_PATTERN = /^[^&=#?%]+$/;

/**
 * Assemble a single composite param into a `key=value` query segment with the
 * exact encoding the partner expects. Missing source values render blank.
 */
export const assembleCompositeParam = (
  composite: PreLeadRedirectCompositeParam,
  values: PreLeadRedirectCompositeSourceValues
): string => {
  const delimiter = encodeURIComponent(composite.delimiter ?? DEFAULT_DELIMITER);
  const labelSeparator = composite.labelSeparator ?? DEFAULT_LABEL_SEPARATOR;
  const value = composite.components
    .map(
      (component) =>
        `${encodeURIComponent(component.label)}${labelSeparator}${encodeURIComponent(
          values[component.source] ?? ""
        )}`
    )
    .join(delimiter);
  return `${composite.paramName}=${value}`;
};

/**
 * Build the `key=value` query segments for every configured composite param.
 * Returns an empty array when none are configured.
 */
export const buildCompositeQuerySegments = (
  composites: PreLeadRedirectCompositeParam[] | null | undefined,
  values: PreLeadRedirectCompositeSourceValues
): string[] => (composites ?? []).map((composite) => assembleCompositeParam(composite, values));

export type ParsedCompositeParamValue = Record<string, string>;

const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/**
 * Inverse of `assembleCompositeParam` for a single composite VALUE (the part
 * after `paramName=`) as a partner echoes it back after one URL-decode — e.g.
 * vega's `original_VendorLeadID` column carrying UMA's `vendorleadid`:
 *
 *   marketplace_event_id:abc123|customer_id:def456|ef_transaction_id:
 *
 * Splits on the literal delimiter (tolerating a still-percent-encoded one in
 * case a feed stores the raw query value), then each part at its FIRST label
 * separator, so a stray separator inside a value stays in the value. Labels
 * and values are decoded with a throw-guarded `decodeURIComponent` (they are
 * encodeURIComponent'd cuids/hex, so decoding is normally a no-op, but a
 * stray `%` in partner-mangled data must not throw) and trimmed. Empty values
 * are preserved as `""` (the builder emits blanks for missing sources);
 * unknown labels are preserved; on duplicate labels the FIRST occurrence
 * wins. Returns null when the input is not a string or contains no labeled
 * part at all — a bare id or free text must never come back looking like a
 * parsed token, because callers lift tokens into identifier slots where a
 * false value would suppress weaker fallbacks.
 */
export const parseCompositeParamValue = (
  raw: unknown,
  opts?: { delimiter?: string; labelSeparator?: string }
): ParsedCompositeParamValue | null => {
  if (typeof raw !== "string" || raw.trim() === "") return null;
  const delimiter = opts?.delimiter ?? DEFAULT_DELIMITER;
  const labelSeparator = opts?.labelSeparator ?? DEFAULT_LABEL_SEPARATOR;

  const safeDecode = (part: string): string => {
    try {
      return decodeURIComponent(part);
    } catch {
      return part;
    }
  };

  let parts = raw.split(delimiter);
  if (parts.length === 1) {
    const encodedDelimiter = encodeURIComponent(delimiter);
    if (encodedDelimiter !== delimiter) {
      parts = raw.split(new RegExp(escapeRegExp(encodedDelimiter), "i"));
    }
  }

  // Null prototype so hostile labels like "__proto__" become plain own
  // properties instead of touching the prototype chain.
  const parsed: ParsedCompositeParamValue = Object.create(null);
  let labeledParts = 0;
  for (const part of parts) {
    const separatorIndex = part.indexOf(labelSeparator);
    if (separatorIndex <= 0) continue;
    const label = safeDecode(part.slice(0, separatorIndex)).trim();
    if (label === "") continue;
    labeledParts += 1;
    if (label in parsed) continue;
    parsed[label] = safeDecode(part.slice(separatorIndex + labelSeparator.length)).trim();
  }
  return labeledParts > 0 ? parsed : null;
};

/**
 * Validate raw composite-param config (e.g. from the admin JSON field or a
 * GraphQL mutation). Returns a list of human-readable errors; an empty list
 * means the value is valid. `null`/`undefined` is valid (no composites).
 */
export const validatePreLeadRedirectCompositeParams = (value: unknown): string[] => {
  const errors: string[] = [];
  if (value == null) return errors;
  if (!Array.isArray(value)) {
    return ["preLeadRedirectCompositeParams must be an array."];
  }

  const seenParamNames = new Set<string>();
  value.forEach((raw, index) => {
    const where = `composite param #${index + 1}`;
    if (typeof raw !== "object" || raw === null || Array.isArray(raw)) {
      errors.push(`${where} must be an object.`);
      return;
    }
    const { paramName, delimiter, labelSeparator, components } = raw as Record<string, unknown>;

    if (typeof paramName !== "string" || !PARAM_NAME_PATTERN.test(paramName)) {
      errors.push(`${where}: "paramName" must match ${PARAM_NAME_PATTERN.source}.`);
    } else if (seenParamNames.has(paramName)) {
      errors.push(`${where}: duplicate paramName "${paramName}".`);
    } else {
      seenParamNames.add(paramName);
    }

    if (delimiter !== undefined && (typeof delimiter !== "string" || delimiter.length === 0)) {
      errors.push(`${where}: "delimiter" must be a non-empty string when provided.`);
    }
    if (
      labelSeparator !== undefined &&
      (typeof labelSeparator !== "string" || !LABEL_SEPARATOR_PATTERN.test(labelSeparator))
    ) {
      errors.push(
        `${where}: "labelSeparator" must be a non-empty string without & = # ? % characters.`
      );
    }

    if (!Array.isArray(components) || components.length === 0) {
      errors.push(`${where}: "components" must be a non-empty array.`);
      return;
    }
    components.forEach((rawComponent, componentIndex) => {
      const componentWhere = `${where} component #${componentIndex + 1}`;
      if (typeof rawComponent !== "object" || rawComponent === null || Array.isArray(rawComponent)) {
        errors.push(`${componentWhere} must be an object.`);
        return;
      }
      const { label, source } = rawComponent as Record<string, unknown>;
      if (typeof label !== "string" || label.length === 0) {
        errors.push(`${componentWhere}: "label" must be a non-empty string.`);
      }
      if (
        typeof source !== "string" ||
        !PRE_LEAD_REDIRECT_COMPOSITE_SOURCES.includes(source as PreLeadRedirectCompositeSource)
      ) {
        errors.push(
          `${componentWhere}: "source" must be one of ${PRE_LEAD_REDIRECT_COMPOSITE_SOURCES.join(
            ", "
          )}.`
        );
      }
    });
  });

  return errors;
};
